/**
 * Getting panels ready for Instagram, in the browser: every image in an Instagram
 * carousel is cropped to the first one's shape, and Instagram only takes JPEG. So each
 * panel is drawn centred on a white JPEG of one shared shape, then uploaded.
 */

import { imageUrl } from './format'
import { ComicImage } from './types'
import { uploadImage } from './uploads'

export const MAX_PANELS = 10
const WIDTH = 1440
/** Instagram allows shapes from 4:5 (portrait) to 1.91:1 (landscape) */
const TALLEST = 4 / 5
const WIDEST = 1.91

/** The shared shape: the first panel's, kept within what Instagram allows */
export function instagramSize(width: number, height: number): { width: number; height: number } {
  const ratio = Math.min(WIDEST, Math.max(TALLEST, width / height))
  return { width: WIDTH, height: Math.round(WIDTH / ratio) }
}

async function loadPanel(key: string): Promise<ImageBitmap> {
  const res = await fetch(imageUrl(key))
  if (!res.ok) throw new Error(`Panel ${key} couldn't be loaded`)
  return createImageBitmap(await res.blob())
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("The image couldn't be made"))), 'image/jpeg', 0.9)
  )
}

/** Pad each panel to one shape on white, upload the JPEGs, and return their keys */
export async function prepareInstagramImages(
  images: ComicImage[],
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal
): Promise<string[]> {
  const panels = images.slice(0, MAX_PANELS)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot prepare images')
  const keys: string[] = []
  let size: { width: number; height: number } | null = null

  for (const [i, panel] of panels.entries()) {
    onProgress(i, panels.length)
    const bitmap = await loadPanel(panel.key)
    size ??= instagramSize(bitmap.width, bitmap.height)
    canvas.width = size.width
    canvas.height = size.height
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, size.width, size.height)
    const scale = Math.min(size.width / bitmap.width, size.height / bitmap.height)
    const w = Math.round(bitmap.width * scale)
    const h = Math.round(bitmap.height * scale)
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bitmap, Math.round((size.width - w) / 2), Math.round((size.height - h) / 2), w, h)
    bitmap.close()
    const blob = await toJpeg(canvas)
    const file = new File([blob], `instagram-${i + 1}.jpg`, { type: 'image/jpeg' })
    keys.push(await uploadImage(file, () => {}, signal))
  }
  onProgress(panels.length, panels.length)
  return keys
}
